import { Box3, Vector3 } from "three";

/**
 * Measures a loaded car model and works out the transform that puts it where
 * the rest of the scene expects a car to be.
 *
 * Every other part of the rig — the studio lighting rig's Lightformer
 * positions, the camera's height and dolly range, the framing solve — is
 * authored in metres around a car standing on y=0 and centred on the origin.
 * A GLB, though, arrives in whatever units and offset it was modelled in.
 * Dropping that in raw would leave the car many times the size of the
 * light rig, and pivoting it for the reveal would swing it around a point
 * that isn't its own centre.
 *
 * So rather than hard-coding numbers for one particular file, the model is
 * measured on load and normalised. Swapping in a different car means
 * dropping in the file — the framing, lighting, and rotation all keep
 * working.
 */

/** Overall length, in metres, every car model is scaled to. A hatchback of
 * this class is a little over four metres nose to tail. */
export const CAR_TARGET_LENGTH = 4.3;

const cache = new WeakMap();

/**
 * Measured once per loaded scene and cached: `useGLTF` hands back the same
 * scene object every render, and walking every vertex of the model on each
 * frame would be wasteful.
 */
export function measureCar(scene) {
  const cached = cache.get(scene);
  if (cached) return cached;

  const box = new Box3().setFromObject(scene);
  const size = box.getSize(new Vector3());
  const centre = box.getCenter(new Vector3());

  // Length is the model's longest horizontal axis. Taking the larger of X/Z
  // rather than assuming Z means a model authored along either axis
  // normalises to the same real-world size.
  const length = Math.max(size.x, size.z);
  const scale = length > 0 ? CAR_TARGET_LENGTH / length : 1;

  const geometry = {
    scale,
    offset: [-centre.x * scale, -box.min.y * scale, -centre.z * scale],
    half: { x: (size.x * scale) / 2, y: (size.y * scale) / 2, z: (size.z * scale) / 2 },
  };

  cache.set(scene, geometry);
  return geometry;
}
